/**
 * Image URL rewriter that inserts a `_WxH` size hint into Shopify CDN
 * URLs so our product grid downloads a ~400px thumbnail instead of the
 * native 1200-2000px original. Non-Shopify URLs (Cloudflare R2 uploads,
 * Emergent customer assets, etc.) are returned unchanged — safe to drop
 * in anywhere a product image renders.
 *
 * Shopify's CDN natively supports `_400x400` / `_600x600` / etc. before
 * the file extension:
 *   original:  cdn.shopify.com/s/files/…/foo.jpg?v=123
 *   resized:   cdn.shopify.com/s/files/…/foo_400x400.jpg?v=123
 * This is documented + stable and needs no API token.
 *
 * Standard preset sizes:
 *   'thumb'  → 240×240 (mini grid cards, ProductRow)
 *   'card'   → 400×400 (main product grid tile)
 *   'detail' → 800×800 (product page main image)
 *   'zoom'   → 1600×1600 (magnifier)
 * Pass a raw `WxH` string like `'720x720'` for custom sizes.
 */
const PRESETS = {
  thumb: '240x240',
  card: '400x400',
  detail: '800x800',
  zoom: '1600x1600',
};

const SHOPIFY_HOSTS = ['cdn.shopify.com', 'cdn.shopifycdn.net'];

/**
 * Resize a Shopify CDN URL to the given preset (or raw WxH).
 * @param {string} url source URL
 * @param {string} sizeOrPreset e.g. 'card' | 'detail' | '720x720'
 * @returns {string} rewritten URL (or the original if not a Shopify host)
 */
export function shopifyImg(url, sizeOrPreset = 'card') {
  if (!url || typeof url !== 'string' || !url.trim()) return '';
  let host;
  try { host = new URL(url).host; } catch { return url; }
  if (!SHOPIFY_HOSTS.includes(host)) return url;

  const dim = PRESETS[sizeOrPreset] || sizeOrPreset;
  // Insert the `_WxH` suffix before the extension. Preserves querystring.
  return url.replace(/_\d+x\d+(?=\.[a-zA-Z]{3,4}(?:\?|$))/, '').replace(/(\.[a-zA-Z]{3,4})(\?.*)?$/, (_, ext, qs) => `_${dim}${ext}${qs || ''}`);
}

/**
 * Convenience helper — same as shopifyImg() but with a defensive
 * fallback to the empty string when the caller might pass undefined.
 */
export function productThumb(url, sizeOrPreset = 'card') {
  return shopifyImg(url, sizeOrPreset) || undefined;
}

export function productSrcSet(url) {
  try {
    if (!SHOPIFY_HOSTS.includes(new URL(url).host)) return undefined;
    return [240, 400, 600, 800].map(w => `${shopifyImg(url, `${w}x${w}`)} ${w}w`).join(', ');
  } catch { return undefined; }
}
